import React from 'react';
import { Chip, ChipProps, SxProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { typographyTokens } from '../../theme/typography-tokens';

// Type for MUI Chip variants matching the new theme system
export type BadgeVariant =
  | 'primary-solid'
  | 'primary-outline'
  | 'primary-subtle'
  | 'accent-solid'
  | 'accent-outline'
  | 'accent-subtle'
  | 'neutral-solid'
  | 'neutral-outline'
  | 'neutral-subtle'
  | 'success-solid'
  | 'success-outline'
  | 'success-subtle'
  | 'warning-solid'
  | 'warning-outline'
  | 'warning-subtle'
  | 'error-solid'
  | 'error-outline'
  | 'error-subtle'
  | 'medium-solid'
  | 'medium-outline'
  | 'medium-subtle'
  | 'info-solid'
  | 'info-outline'
  | 'info-subtle';

export interface BadgeProps
  extends Omit<ChipProps, 'children' | 'label' | 'variant' | 'size' | 'color'>, PounceStyleProps {
  /** Additional styles to apply (combined with pounce props) */
  sx?: ChipProps['sx'];

  /** Badge variant combining color and style. Defaults to `primary-solid`. Ignored when color prop is provided. */
  variant?: BadgeVariant;

  /** Whether the Badge should be emphasized or not */
  emphasized?: boolean;

  /** The size of the Badge. Defaults to `medium`. */
  size?: 'small' | 'medium' | 'large';

  /** A fixed size for the badge. Helpful if you have multiple badges and want them to all have the same width */
  width?: string | number;

  /** The content to display inside the badge */
  children: React.ReactNode;

  /** Controls the border style */
  borderStyle?: 'pill' | 'rectangle';
}

/**
 * Badge component built on top of MUI Chip.
 *
 * A badge is a visual label to accompany & characterize text content.
 * Provides all the functionality from the original Pounce Badge including
 * color theming, sizing, emphasis, and styling variants.
 *
 * Features:
 * - Size variants (small, medium, large) via theme
 * - Combined color + style variants (e.g., primary-solid, accent-outline, success-subtle)
 * - Border styles (rectangle, pill)
 * - Color options (primary, accent, neutral, success, warning, error, medium, info)
 * - Style options (solid, outline, subtle)
 * - Emphasis support
 * - Full pounce style props compatibility
 * - Theme-based styling and color resolution
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  {
    emphasized = false,
    size = 'medium',
    borderStyle = 'pill',
    width,
    variant = 'primary-solid',
    children,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const combinedSx = {
    ...pounceStyles,
    ...(emphasized && {
      fontWeight: typographyTokens.fontWeights.semibold,
      textTransform: 'uppercase',
    }),

    width: width || 'fit-content',

    borderRadius: borderStyle === 'pill' ? '100px' : '4px',

    ...sx,
  } as SxProps;

  return (
    <Chip
      ref={ref}
      label={children}
      size={size}
      variant={variant}
      role="status"
      aria-atomic="true"
      sx={combinedSx}
      {...otherProps}
    />
  );
});
